import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Section, Container, Eyebrow, RevealHeading, Reveal } from "@/components/primitives";
import { PageHero } from "@/components/PageHero";
import { CTASection } from "@/components/CTASection";
import { Seo } from "@/components/Seo";
import { IMAGES, MODEL } from "@/data/site";

const DOMAINS = [
  { n: "01", key: "Govern", title: "Governance & Institutional Transformation", to: "/government" },
  { n: "02", key: "Deliver", title: "Programme & Project Delivery", to: "/government" },
  { n: "03", key: "Engage", title: "Citizen & Stakeholder Experience", to: "/government" },
  { n: "04", key: "Organise", title: "Political Strategy & Operations", to: "/political" },
  { n: "05", key: "Digital", title: "Technology & AI", to: "/digital" },
  { n: "06", key: "Measure", title: "Research, Intelligence & Impact", to: "/impact" },
];

const FORMAT = [
  "What is it?",
  "The problem",
  "What GovX does",
  "How we work",
  "What you get",
  "The outcome",
  "Who it's for",
  "Related capabilities",
  "Talk to GovX",
];

export default function WhatWeDo() {
  return (
    <>
      <Seo
        title="What We Do | GovX Nigeria"
        description="GovX Nigeria works across six connected domains — governance, delivery, citizen experience, political operations, technology and measurement — using one consistent operating methodology."
      />
      <PageHero
        eyebrow="What We Do"
        title={["An Operating", "Methodology —", "Not A Service List."]}
        copy="We work across the entire journey from idea to impact. Every engagement follows one consistent GovX format — so it feels like a methodology, not a menu."
        cta="Explore Our Capabilities"
        ctaTo="/government"
        image={IMAGES.boardroom}
        kicker="From ambition to execution"
      />

      {/* Six domains */}
      <Section className="py-24 lg:py-32">
        <Container>
          <Eyebrow>Six Connected Domains</Eyebrow>
          <RevealHeading
            lines={["From Strategy To Execution."]}
            as="h2"
            className="mt-7 font-display text-4xl font-medium tracking-tight sm:text-5xl"
          />
          <div className="mt-14 grid gap-px overflow-hidden border border-govx-charcoal/10 bg-govx-charcoal/10 md:grid-cols-2 lg:grid-cols-3">
            {DOMAINS.map((d, i) => (
              <motion.div
                key={d.key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.55, delay: i * 0.07 }}
              >
                <Link
                  to={d.to}
                  data-testid={`domain-${d.key.toLowerCase()}`}
                  className="group flex h-full flex-col bg-govx-paper p-8 transition-colors duration-500 hover:bg-govx-charcoal hover:text-govx-paper"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs text-govx-green group-hover:text-govx-lime">{d.n}</span>
                    <ArrowRight className="h-4 w-4 text-govx-grey transition-transform duration-300 group-hover:translate-x-1 group-hover:text-govx-lime" />
                  </div>
                  <h3 className="mt-8 font-display text-2xl font-medium tracking-tight">{d.key}</h3>
                  <p className="mt-3 text-sm text-govx-charcoal/60 group-hover:text-govx-paper/70">{d.title}</p>
                </Link>
              </motion.div>
            ))}
          </div>
        </Container>
      </Section>

      {/* GovX service format */}
      <Section dark className="py-24 lg:py-32">
        <Container>
          <div className="grid gap-14 lg:grid-cols-[0.9fr_1.1fr]">
            <div>
              <Eyebrow dark>The GovX Service Format</Eyebrow>
              <RevealHeading
                lines={["Consistent.", "Practical.", "Repeatable."]}
                as="h2"
                className="mt-7 font-display text-4xl font-medium leading-[1.02] tracking-tight sm:text-5xl"
              />
              <Reveal delay={0.1}>
                <p className="mt-7 max-w-md text-base leading-relaxed text-govx-grey">
                  Every service explains the problem, what GovX actually does, how the engagement
                  works and what the client gets. That consistency is what makes GovX an operating
                  methodology.
                </p>
              </Reveal>
            </div>
            <div className="grid gap-px overflow-hidden border border-white/10 bg-white/5 sm:grid-cols-3">
              {FORMAT.map((f, i) => (
                <motion.div
                  key={f}
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.06 }}
                  className="bg-govx-black p-6"
                >
                  <div className="font-mono text-xs text-govx-lime">{String(i + 1).padStart(2, "0")}</div>
                  <div className="mt-4 font-display text-base font-medium text-govx-paper">{f}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </Container>
      </Section>

      {/* Model */}
      <Section className="py-24 lg:py-32">
        <Container>
          <Eyebrow>How We Work</Eyebrow>
          <RevealHeading
            lines={["Diagnose. Design. Deliver.", "Measure. Improve."]}
            as="h2"
            className="mt-7 font-display text-3xl font-medium tracking-tight sm:text-5xl"
          />
          <div className="mt-14 grid gap-px overflow-hidden border border-govx-charcoal/10 bg-govx-charcoal/10 md:grid-cols-5">
            {MODEL.map((step, i) => (
              <motion.div
                key={step.n}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="group bg-govx-paper p-7 transition-colors duration-500 hover:bg-govx-charcoal hover:text-govx-paper"
              >
                <div className="font-mono text-4xl font-light text-govx-grey group-hover:text-govx-lime">{step.n}</div>
                <h3 className="mt-6 font-display text-xl font-medium tracking-tight">{step.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-govx-charcoal/60 group-hover:text-govx-paper/70">{step.body}</p>
              </motion.div>
            ))}
          </div>
        </Container>
      </Section>

      <CTASection copy="We don't just advise. We help deliver." />
    </>
  );
}
